import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  FileText,
  User,
  Calendar,
  X,
  ChevronRight,
  MessageSquare,
  Smartphone,
  Clock,
  Users,
  FileCheck,
  BarChart4,
  ClipboardList,
  ClipboardCheck,
  MapPin,
  DollarSign,
  Edit,
  Check,
  Share2,
  Ruler,
  RefreshCw,
  Loader2,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { getCurrentTenantId } from "@/utils/tenantUtils";
import { projectService } from "@/services/projectService";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/components/ui/use-toast";
import { ExtendedProject } from "@/types/project";
import { usePermissions } from "@/contexts/PermissionContext";
import { useContractsFeatureAccess } from "@/hooks/useContractsFeatureAccess";
import ReactDOM from "react-dom";
import { TaskDetails } from "@/components/tasks/TaskList";
import { Task, taskService, CreateTaskInput } from "@/services/taskService";
import { TaskModal } from "@/components/tasks/TaskModal";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from "@/components/ui/tooltip";
import { notifyProjectFollowers } from "@/services/notificationService";
import { ProjectFormData } from "@/pages/NewProject";
import { SlidePanel } from "@/components/ui/slide-panel";
import { ProjectModalSkeleton } from "./ProjectModalSkeleton";
// SUB-4.4 · FR38. The open record says which account it belongs to.
import { useTenant } from "@/contexts/TenantContext";
import { buildAccountNames } from "@/lib/accountAttribution";
import { recordAccountBarState } from "@/lib/recordAccountBar";
import { RecordAccountBar } from "@/components/scope/RecordAccountBar";
import {
  useCanRunBackfill,
  useStartBackfill,
} from "@/hooks/integrations/useBackfillProgress";
import { PipelineTimeline } from "@/components/project/PipelineTimeline";
import { useStageHistory } from "@/hooks/useStageHistory";
import { DocumentGalleryViewer } from "@/components/documents/DocumentGalleryViewer";
import { ProjectModalSidebar } from "@/components/project/ProjectModalSidebar";
import { ProjectModalTabRenderer } from "@/components/project/ProjectModalTabRenderer";
import {
  ProjectStatusPanel,
  ProjectStagePanel,
  ProjectAssignmentPanels,
} from "@/components/project/panels";
import { useProjectForm } from "@/hooks/useProjectForm";
import { useProjectDocuments } from "@/hooks/useProjectDocuments";
import { useProjectAssignments } from "@/hooks/useProjectAssignments";
import { useProjectStateManagement } from "@/hooks/useProjectStateManagement";
import { useProjectData } from "@/hooks/useProjectData";
import { DrawerStackProvider, useDrawerStack } from "@/contexts/DrawerStackContext";
import { DrawerRenderer } from "@/components/drawers";
import { usePartnerScoped } from "@/hooks/usePartnerScoped";
import { canWriteProject, PARTNER_PROJECT_READ_ONLY_NOTE } from "@/lib/partnerScope";
import { canReadContracts } from "@/lib/contractAccess";

type ProjectModalProps = {
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onDelete?: () => void;
  defaultTab?: string | null;
  defaultCommentTab?: "internal" | "external";
};

/**
 * Content area wrapper that disables scrolling when drawers are open.
 * Must be used inside DrawerStackProvider.
 */
interface ModalContentAreaProps {
  activeTab: string;
  children: React.ReactNode;
}

function ModalContentArea({ activeTab, children }: ModalContentAreaProps) {
  const { stackDepth } = useDrawerStack();
  const hasOpenDrawers = stackDepth > 0;

  return (
    <div
      className={`flex-1 relative ${
        activeTab === "communication" || activeTab === "sms"
          ? "flex flex-col min-h-0 overflow-y-hidden"
          : hasOpenDrawers
            ? "overflow-hidden px-4 pt-4 pb-4"
            : "overflow-y-auto px-4 pt-4 pb-4"
      }`}
    >
      {children}
    </div>
  );
}

export function ProjectModal({
  projectId,
  isOpen,
  onClose,
  onDelete,
  defaultTab = null,
  defaultCommentTab,
}: ProjectModalProps) {
  const { user } = useAuth();
  const {
    hasPermission,
    canMoveStageDirectly,
    loading: permissionLoading,
  } = usePermissions();
  // CB-8.1: tenant feature gate — whether the current user's role may use Contracts at all.
  // Composes on top of the existing permission/pipeline/stage gate for the Contracts tab (UX-DR22).
  const { hasAccess: hasContractsFeature } = useContractsFeatureAccess();
  // RBAC-3.93: a partner-scoped caller reads this project and writes nothing on it (RBAC-3.90's
  // RLS refuses it), so every edit, status, owner and follower control below is withheld.
  const partnerScope = usePartnerScoped();
  const canWrite = canWriteProject(partnerScope);
  const canEditProject =
    canWrite && (hasPermission("edit_projects") ||
    hasPermission("manage_projects") ||
    hasPermission("project.manage_all") ||
    hasPermission("system.override") ||
    hasPermission("tenant.manage"));

  // Helper to check if the current user has management permissions
  const hasManagementPermissions = () => {
    return canWrite && (
      hasPermission("settings.admin") ||
      hasPermission("manage_roles") ||
      hasPermission("manage_users") ||
      hasPermission("manage_projects") ||
      hasPermission("project.manage_all") ||
      hasPermission("edit_projects") ||
      hasPermission("system.override") ||
      hasPermission("tenant.manage")
    );
  };

  // SUB-4.4 · FR38. Which account this record belongs to, named from the
  // picker's own tenant list rather than from a `projects → tenants` join:
  // `tenants_select` is still single-tenant, so a join returns the anchor's name
  // and NULL for every sub-account. Same source and same reasoning as the
  // merged list's Account column (`accountAttribution.ts`).
  const { mergedScope, userTenants } = useTenant();
  const accountNames = useMemo(() => buildAccountNames(userTenants), [userTenants]);

  // Track if modal has been opened at least once - now in useProjectData hook

  // State to store user's role_id
  const [userRoleId, setUserRoleId] = useState<number | null>(null);

  const onTaskSubmitted = async () => {};

  // Read tab from URL if present, or use defaultTab if provided
  const [activeTab, setActiveTab] = useState(defaultTab || "opportunity");
  const [tabLoaded, setTabLoaded] = useState<{ [tab: string]: boolean }>({
    opportunity: true,
    tasks: true,
    "project-tasks": true,
    finance: true,
    design: true,
    docs: true,
    communication: true,
    sms: true,
    ...(defaultTab ? { [defaultTab]: true } : {}),
    notes: true,
    timeline: true,
    "site-survey": true,
    "book-consultation": true,
    "sales-manager": true,
    "new-project-form": true,
    "sales-manager-finance": true,
    "work-orders": true,
    contracts: true,
  });

  // Update activeTab when defaultTab prop changes (e.g., from Quick Info buttons)
  useEffect(() => {
    if (defaultTab && defaultTab !== activeTab) {
      setActiveTab(defaultTab);
      // Mark the tab as loaded so it renders immediately
      setTabLoaded(prev => ({ ...prev, [defaultTab]: true }));
    }
  }, [defaultTab]);

  const [isManualTabChange, setIsManualTabChange] = useState(false);
  const hasSetInitialTab = useRef(false);
  // Capture initial values to prevent re-running effect when they change
  const initialFormCompleted = useRef(false);
  const initialPipelineId = useRef<number | null>(null);
  // Guard to prevent duplicate fetchProject calls is now in useProjectData hook

  const [siteSurveyCompletionDate, setSiteSurveyCompletionDate] = useState<
    string | null
  >(null);
  // loading, contentLoaded, error, project, recentlySaved are now in useProjectData hook
  // Form state is now managed by useProjectForm hook (see below)
  const hasLeftFormTab = useRef(false);

  // Define required fields for the new project form
  const requiredFields = [
    "pipeline_id",
    "company_name",
    "entity_type",
    "first_name",
    "last_name",
    "contact_email",
    "contact_phone",
    "project_street",
    "project_city",
    "project_state",
    "project_zip_code",
    "system_type",
    "utility_provider",
  ];

  // Ref to hold form sync callback (set after projectForm hook is initialized)
  const formSyncRef = useRef<{
    setFormValues: React.Dispatch<React.SetStateAction<Partial<ExtendedProject>>>;
    setOriginalFormValues: React.Dispatch<React.SetStateAction<Partial<ExtendedProject>>>;
    originalFormValuesRef: React.MutableRefObject<Partial<ExtendedProject>>;
    setHasUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
  } | null>(null);

  // Form sync callback for useProjectData hook
  const handleProjectLoad = useCallback((loadedProject: ExtendedProject) => {
    if (formSyncRef.current) {
      formSyncRef.current.setFormValues(loadedProject);
      formSyncRef.current.setOriginalFormValues(loadedProject);
      formSyncRef.current.originalFormValuesRef.current = loadedProject;
      formSyncRef.current.setHasUnsavedChanges(false);
    }
  }, []);

  // Initialize project data hook first (provides project state and data loading)
  const projectDataHook = useProjectData({
    projectId,
    isOpen,
    onProjectLoad: handleProjectLoad,
  });

  // Destructure project data for backward compatibility
  const {
    project,
    setProject,
    loading,
    setLoading,
    error,
    setError,
    contentLoaded,
    setContentLoaded,
    openTasks,
    setOpenTasks,
    completedTasks,
    setCompletedTasks,
    assigneeNames,
    setAssigneeNames,
    recentlySaved,
    setRecentlySaved,
    recentlySavedTimeoutRef,
    isFetchingProject,
    fetchProjectTimeoutId,
    setFetchProjectTimeoutId,
    hasOpened,
    setHasOpened,
    fetchProject,
    debouncedFetchProject,
  } = projectDataHook;

  // Use the project form hook for form state management
  const projectForm = useProjectForm({
    project: project as ExtendedProject | null,
    projectId,
    userId: user?.id,
    // Pre-save callback: handle tab switching when saving from new-project-form
    onBeforeSave: () => {
      if (activeTab === 'new-project-form') {
        hasLeftFormTab.current = true;
        setActiveTab('opportunity');
        setTabLoaded((prev) => ({ ...prev, opportunity: true }));
        // Update URL to reflect the tab change
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set("tab", "opportunity");
        window.history.replaceState({}, "", newUrl);
      }
    },
    // Post-save callback: set recentlySaved flag to prevent real-time fetch overwrite
    onAfterSave: () => {
      setRecentlySaved(true);
      if (recentlySavedTimeoutRef.current) {
        clearTimeout(recentlySavedTimeoutRef.current);
      }
      recentlySavedTimeoutRef.current = setTimeout(() => {
        setRecentlySaved(false);
        recentlySavedTimeoutRef.current = null;
      }, 5000);
    },
    onSaveSuccess: () => {
      toast({
        title: "Success",
        description: "Project details saved successfully",
      });
    },
    onSaveError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save project details",
        variant: "destructive",
      });
    },
  });

  // Destructure form state for backward compatibility
  const {
    formValues,
    setFormValues,
    originalFormValues,
    setOriginalFormValues,
    originalFormValuesRef,
    hasUnsavedChanges,
    setHasUnsavedChanges,
    formErrors,
    setFormErrors,
    handleInputChange,
    handleSaveOverviewChanges,
    saving,
    saveError,
    saveSuccess,
    setSaving,
    setSaveError,
    setSaveSuccess,
    logChangesToActivityLog,
  } = projectForm;

  // Set up form sync ref for useProjectData callback
  useEffect(() => {
    formSyncRef.current = {
      setFormValues,
      setOriginalFormValues,
      originalFormValuesRef,
      setHasUnsavedChanges,
    };
  }, [setFormValues, setOriginalFormValues, originalFormValuesRef, setHasUnsavedChanges]);

  // State for triggering ProjectManagementTab refresh on document changes
  const [projectManagementRefreshKey, setProjectManagementRefreshKey] = useState(0);

  // Use the project documents hook for document state management
  const projectDocuments = useProjectDocuments({
    projectId,
    userId: user?.id,
    onUploadSuccess: () => {
      // Trigger refresh of ProjectManagementTab to update reference fields
      setProjectManagementRefreshKey((prev) => prev + 1);
    },
    onDeleteSuccess: () => {
      // Trigger refresh of ProjectManagementTab to update reference fields
      setProjectManagementRefreshKey((prev) => prev + 1);
    },
    onFormValueUpdate: (updates) => {
      // Update form values for fields like satellite_image_path
      setFormValues((prev) => ({ ...prev, ...updates }));
    },
  });

  // Destructure document state for backward compatibility
  const {
    documents,
    setDocuments,
    filePreviewUrls,
    setFilePreviewUrls,
    documentMetadata,
    fileLoading,
    uploadStatus,
    documentUpdateTimestamp,
    dragActive,
    galleryState,
    setGalleryState,
    dlVerificationStatus,
    dlVerificationData,
    isVerifying,
    ubVerificationStatus,
    ubVerificationData,
    isVerifyingUb,
    fetchProjectDocuments,
    fetchDocumentCounts,
    fetchFieldDocuments,
    documentCounts,
    countsLoading,
    loadedFields,
    handleFileChange,
    handleRemoveFile,
    handleDrag,
    handleDrop,
    handleVerifyDriversLicense,
    handleVerifyUtilityBill,
    buildGalleryDocuments,
    isImageFileType,
  } = projectDocuments;

  // Initialize project assignments hook
  const projectAssignments = useProjectAssignments({
    projectId,
    project,
    onAssignmentChange: () => {
      // Assignment change triggers real-time event, no need to manually refresh
    },
  });

  // Destructure assignment state - only what's needed for ProjectAssignmentPanels
  // and local uses (eligibleUsers fetch, fetchFollowers calls)
  const {
    panelStates: assignmentPanelStates,
    searchStates: assignmentSearchStates,
    setSearch: setAssignmentSearch,
    openPanel: openAssignmentPanel,
    closePanel: closeAssignmentPanel,
    allUsers,
    eligibleUsers,
    followers,
    setEligibleUsers,
    fetchFollowers,
    handleChangeOwner,
    handleChangeManager,
    handleChangeProposalDesigner,
    handleChangeInstallerPartner,
    handleChangeInstallerPM,
    handleChangeSurveyor,
    handleChangeBizDev,
    handleChangeCloser,
    handleChangeSetter,
    addFollower,
    removeFollower,
  } = projectAssignments;

  // Wrapper functions to open assignment panels (used by child components)
  // These support both direct boolean and functional updates (SetStateAction)
  const setShowChangeOwner: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.owner) : value;
    if (open) openAssignmentPanel("owner"); else closeAssignmentPanel("owner");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.owner]);
  const setShowChangeManager: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.manager) : value;
    if (open) openAssignmentPanel("manager"); else closeAssignmentPanel("manager");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.manager]);
  const setShowChangeProposalDesigner: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.proposal_designer) : value;
    if (open) openAssignmentPanel("proposal_designer"); else closeAssignmentPanel("proposal_designer");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.proposal_designer]);
  const setShowChangeInstallerPartner: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.installer_partner) : value;
    if (open) openAssignmentPanel("installer_partner"); else closeAssignmentPanel("installer_partner");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.installer_partner]);
  const setShowChangeInstallerPM: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.installer_pm) : value;
    if (open) openAssignmentPanel("installer_pm"); else closeAssignmentPanel("installer_pm");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.installer_pm]);
  const setShowChangeSurveyor: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.surveyor) : value;
    if (open) openAssignmentPanel("surveyor"); else closeAssignmentPanel("surveyor");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.surveyor]);
  const setShowChangeBizDev: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.biz_dev) : value;
    if (open) openAssignmentPanel("biz_dev"); else closeAssignmentPanel("biz_dev");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.biz_dev]);
  const setShowChangeCloser: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.closer) : value;
    if (open) openAssignmentPanel("closer"); else closeAssignmentPanel("closer");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.closer]);
  const setShowChangeSetter: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.setter) : value;
    if (open) openAssignmentPanel("setter"); else closeAssignmentPanel("setter");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.setter]);
  const setShowAddFollower: React.Dispatch<React.SetStateAction<boolean>> = useCallback((value) => {
    const open = typeof value === "function" ? value(assignmentPanelStates.followers) : value;
    if (open) openAssignmentPanel("followers"); else closeAssignmentPanel("followers");
  }, [openAssignmentPanel, closeAssignmentPanel, assignmentPanelStates.followers]);

  // Backward-compatible panel state for installer partner useEffect
  const showChangeInstallerPartner = assignmentPanelStates.installer_partner;

  // Initialize project state management hook
  const projectStateManagement = useProjectStateManagement({
    projectId,
    project: project as ExtendedProject | null,
    setProject,
    setSaving,
    setSaveError,
    setSaveSuccess,
  });

  // Destructure state management for backward compatibility
  const {
    showStageDialog,
    showStatusDialog,
    setShowStageDialog,
    setShowStatusDialog,
    stageDropdownOpen,
    statusDropdownOpen,
    setStageDropdownOpen,
    setStatusDropdownOpen,
    stageDropdownRef,
    statusDropdownRef,
    stageDropdownPosition,
    stages,
    setStages,
    handleStatusChange,
    handleStageChange,
    handleMoveToPipelineStage,
    fetchStages,
  } = projectStateManagement;

  // Fetch stage history for timeline visualization
  const { stageHistory } = useStageHistory({
    projectId,
    enabled: isOpen && !!project?.current_stage_id,
  });

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  // openTasks, completedTasks, assigneeNames are now in useProjectData hook
  const { toast } = useToast();

  // SCH-4.3: Sync Now mutation + permission gate for the per-project re-sync
  // button shown next to the Share button on SCH-imported projects.
  const syncNowMutation = useStartBackfill();
  const { canRun: canRunSchSync } = useCanRunBackfill();

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editableTitle, setEditableTitle] = useState("");
  // stages is now managed by useProjectStateManagement hook
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editingTask, setEditingTask] = useState(false);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  // completedTasks state is in useProjectData hook
  const currentUserId = user?.id || "";
  const order = project?.stages?.stage_order;

  // formErrors and updateFormDataFromModal now use hook's state
  const updateFormDataFromModal = useCallback((data: Partial<ProjectFormData>) => {
    setFormValues((prev: Partial<ExtendedProject>) => ({ ...prev, ...data }));
    // Clear errors for updated fields using hook's setFormErrors
    const fieldKeys = Object.keys(data);
    if (fieldKeys.length > 0) {
      setFormErrors((prev) => {
        const newErrors = { ...prev };
        let hasChanges = false;
        fieldKeys.forEach((field) => {
          if (newErrors[field]) {
            delete newErrors[field];
            hasChanges = true;
          }
        });
        return hasChanges ? newErrors : prev;
      });
    }
  }, [setFormValues, setFormErrors]);

  const isFormCompleted = useMemo(() => {
    return formValues.new_project_form_completed || false; // Fallback to false if undefined
  }, [formValues.new_project_form_completed]);

  const tabs = useMemo(() => {
    // Don't render tabs until permissions are loaded
    if (permissionLoading) {
      return [];
    }

    const baseTabs = [
      {
        id: "opportunity",
        name: "Project Overview",
        icon: (
          <FileText className="h-5 w-5 min-w-[20px] text-primary" />
        ),
      },
      {
        id: "design",
        name: "Documents",
        icon: <FileCheck className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "communication",
        name: "Communication",
        icon: <MessageSquare className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "sms",
        name: "SMS",
        icon: <Smartphone className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "notes",
        name: "Project Notes",
        icon: <FileText className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "sales-manager",
        name: "Sales Team Assignments",
        icon: <Users className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "partner",
        name: "Partner Info",
        icon: <Users className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "tasks",
        name: "Project Management",
        icon: <BarChart4 className="h-5 w-5 min-w-[20px] text-primary" />,
      },
    ];

    // Add Consultations tab for all projects
    baseTabs.push({
      id: "book-consultation",
      name: "Consultations",
      icon: <Calendar className="h-5 w-5 min-w-[20px] text-primary" />,
    });

    // Add contracts tab right after Consultations if conditions are met
    if (
      [1, 517].includes(project?.stages?.pipeline_id ?? 0) &&
      (project?.stages?.stage_order ?? 0) >= 10 &&
      hasContractsFeature &&
      // RBAC-7.3: exactly what contracts_select asks, so the tab never opens onto an empty list.
      canReadContracts(hasPermission)
    ) {
      baseTabs.push({
        id: "contracts",
        name: "Contracts",
        icon: <FileText className="h-5 w-5 min-w-[20px] text-primary" />,
      });
    }

    // Continue with other tabs
    baseTabs.push(
      {
        id: "site-survey",
        name: "Site Survey",
        icon: <ClipboardList className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "finance",
        name: "Finance",
        icon: <DollarSign className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "work-orders",
        name: "Work Orders",
        icon: <ClipboardList className="h-5 w-5 min-w-[20px] text-primary" />,
      }, // Added Work Orders tab
      {
        id: "sales-manager-finance",
        name: "Sales Manager Finance",
        icon: <DollarSign className="h-5 w-5 min-w-[20px] text-primary" />,
      },
      {
        id: "timeline",
        name: "Activity",
        icon: <Calendar className="h-5 w-5 min-w-[20px] text-primary" />,
      }
    );

    // Add commercial designs tab for commercial projects
    if (
      formValues?.project_type === "Commercial" ||
      formValues?.project_type === "commercial"
    ) {
      baseTabs.push({
        id: "commercial-designs",
        name: "Designs",
        icon: <Ruler className="h-5 w-5 min-w-[20px] text-primary" />,
      });
    }
    if (
      !formValues.new_project_form_completed &&
      [1, 57].includes(formValues.pipeline_id || 0)
    ) {
      baseTabs.unshift({
        id: "new-project-form",
        name: "New Project Form",
        icon: <ClipboardCheck className="h-5 w-5 min-w-[20px] text-primary" />,
      });
    }

    return baseTabs;
  }, [
    formValues.new_project_form_completed,
    formValues.pipeline_id,
    project?.stages?.pipeline_id,
    project?.stages?.stage_order,
    defaultTab,
    permissionLoading,
    hasContractsFeature,
  ]);

  // stageDropdownPosition, showStageDialog, showStatusDialog and related setters
  // are now managed by useProjectStateManagement hook

  // fetchProjectTimeoutId, debouncedFetchProject, and fetchProject are now in useProjectData hook

  // fetchProject function is now in useProjectData hook
  // Note: fetchFollowers is now provided by useProjectAssignments hook

  // Fetch user's role_id
  useEffect(() => {
    const fetchUserRole = async () => {
      if (user?.id) {
        const { data, error } = await supabase
          .from("users")
          .select("role_id")
          .eq("id", user.id)
          .single();

        if (data && !error) {
          setUserRoleId(data.role_id);
        }
      }
    };

    fetchUserRole();
  }, [user]);

  useEffect(() => {
    setTabLoaded((prev) => ({ ...prev, [activeTab]: true }));
  }, [activeTab]);

  // State to hold the derived commentTab value from URL
  const [derivedCommentTab, setDerivedCommentTab] = useState<"internal" | "external" | undefined>(defaultCommentTab);

  useEffect(() => {
    // Don't override if defaultTab was provided (it takes priority)
    if (!isOpen || defaultTab) return;

    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam && tabParam !== activeTab) {
      // Don't switch back to new-project-form if we've intentionally left it
      if (tabParam === 'new-project-form' && hasLeftFormTab.current) {
        return;
      }
      setActiveTab(tabParam);
      setTabLoaded((prev) => ({ ...prev, [tabParam]: true }));
    }

    // Read commentTab parameter from URL
    const commentTabParam = params.get("commentTab");
    if (commentTabParam && (commentTabParam === "internal" || commentTabParam === "external")) {
      setDerivedCommentTab(commentTabParam);
    }
  }, [isOpen, defaultTab, activeTab]);

  // Update derivedCommentTab when modal opens with new defaultCommentTab
  useEffect(() => {
    if (isOpen && defaultCommentTab) {
      setDerivedCommentTab(defaultCommentTab);
    }
  }, [isOpen, defaultCommentTab]);

  // Signal modal state globally to prevent theme interference
  useEffect(() => {
    if (isOpen) {
      // Set global flag to block ALL theme operations
      (window as any).__JOULES_MODAL_OPEN = true;

      return () => {
        // Keep the modal flag active for longer to prevent any theme changes
        // during the modal close transition
        setTimeout(() => {
          (window as any).__JOULES_MODAL_OPEN = false;
        }, 500);
      };
    }
  }, [isOpen]);

  // Fetch eligible users for closer/setter (team-based filtering)
  useEffect(() => {
    const fetchEligibleUsers = async () => {
      if (!project?.assigned_sales_rep_id) {
        setEligibleUsers([]);
        return;
      }
      try {
        // Step 1: Fetch the team_id of the project owner
        const { data: teamData, error: teamError } = await supabase
          .from('user_teams')
          .select('team_id')
          .eq('user_id', project.assigned_sales_rep_id)
          .single();
        if (teamError || !teamData) {
          console.error('Error fetching project owner team:', teamError);
          setEligibleUsers([]);
          return;
        }

        // Step 2: Fetch all user_ids in that team
        const { data: teamMemberData, error: teamMemberError } = await supabase
          .from('user_teams')
          .select('user_id')
          .eq('team_id', teamData.team_id);
        if (teamMemberError) {
          console.error('Error fetching team members:', teamMemberError);
          setEligibleUsers([]);
          return;
        }

        const userIds = teamMemberData.map(member => member.user_id);

        // Step 3: Fetch users in same team with role_id 1 or 2 for current tenant
        const tenantId = await getCurrentTenantId();
        const { data: usersData, error: usersError } = await supabase
          .from('users')
          .select('id, first_name, last_name, email, role_id, avatar_url, roles(name)')
          .in('id', userIds)
          .in('role_id', [1, 2])
          .eq('tenant_id', tenantId);
        if (usersError) {
          console.error('Error fetching eligible users:', usersError);
          setEligibleUsers([]);
          return;
        }

        setEligibleUsers(usersData || []);
      } catch (err) {
        console.error('Unexpected error fetching eligible users:', err);
        setEligibleUsers([]);
      }
    };
    fetchEligibleUsers();
  }, [project?.assigned_sales_rep_id]);

  // Note: fetchFollowers and fetchAllUsers are now handled by useProjectAssignments hook
  // The hook automatically fetches followers when projectId changes
  // and fetches all users when assignment panels are opened

  // Fetch installer partners when installer partner panel opens
  const fetchInstallerPartners = useCallback(async () => {
    // EPC partner companies are not installers; an installer picker never offers one.
    const { data } = await supabase
      .from("installer_partners")
      .select("*")
      .eq("partner_type", "INSTALLER")
      .order("company_name");
    return data || [];
  }, []);

  useEffect(() => {
    if (showChangeInstallerPartner) {
      fetchInstallerPartners();
    }
  }, [showChangeInstallerPartner, fetchInstallerPartners]);

  // Fetch documents from the database

  // Using updateDocuments instead of _setProjectDocuments to avoid redeclaration
  const updateDocuments = (docs: Record<string, any[]>) => {
    // This function is used to update the internal state of documents
    // before creating preview URLs
    setFormValues((prev) => ({
      ...prev,
      projectDocuments: docs,
    }));
    // Also update the documents state directly
    setDocuments(docs);
  };

  // Fetch the approved work order file from the checklist item
  const fetchApprovedWorkOrderFile = useCallback(async (projectId: string) => {
    if (!projectId) return;

    try {
      // Fetch the checklist item for Approved Work Order (template_item_id: 354)
      const { data: checklistItems, error } = await supabase
        .from("project_checklist_items")
        .select("*")
        .eq("project_id", projectId)
        .eq("template_item_id", 354); // ID for 'Approved Work Order to Include Full Scope of Work and Pricing'

      if (error) {
        return;
      }

      // Check if we have any items
      if (!checklistItems || checklistItems.length === 0) {
        return;
      }

      // Use the first item if multiple exist
      const checklistItem = checklistItems[0];

      // Safely parse the value field
      let fileData;
      try {
        if (typeof checklistItem.value === "string") {
          const parsedValue = JSON.parse(checklistItem.value);
          fileData =
            parsedValue.value && Array.isArray(parsedValue.value)
              ? parsedValue.value[0]
              : null;
        } else if (
          checklistItem.value?.value &&
          Array.isArray(checklistItem.value.value) &&
          checklistItem.value.value.length > 0
        ) {
          fileData = checklistItem.value.value[0];
        }
      } catch (parseError) {
        console.error("Error parsing checklist item value:", parseError);
        return;
      }

      if (!fileData || !fileData.storagePath) {
        return;
      }

      // ⚠️ THIS PREVIEW HAS NEVER WORKED, AND THAT IS FINDING F-69.
      // It asked for a public URL from a bucket named "project-files". There is no such
      // bucket — the 19 that exist do not include it — so this produced a URL that 404s,
      // every time, since it was written.
      //
      // Nothing reported it because `getPublicUrl` CANNOT FAIL: it is string
      // concatenation, it returns happily for any bucket name at all, and the truthiness
      // check below it was therefore always true. That is the same property that let F-17
      // sit open for months, and it is why the empty `else {}` branch never fired.
      //
      // Signed, against the bucket the file is actually in, and an error is now an error.
      const { data: signed, error: signError } = await supabase.storage
        .from("project-documents")
        .createSignedUrl(fileData.storagePath, 3600);

      // The path is recorded either way: it is the durable reference, and the form should
      // not lose it just because a preview could not be rendered.
      setFormValues((prev) => ({
        ...prev,
        approved_work_order_path: fileData.storagePath,
      }));

      if (signError || !signed?.signedUrl) {
        console.error(
          "Failed to sign approved work order file:",
          fileData.storagePath,
          signError,
        );
      } else {
        setFilePreviewUrls((prev) => ({
          ...prev,
          approved_work_order_file: [signed.signedUrl],
        }));
      }
    } catch (err) {
      console.error("Error fetching approved work order file:", err);
    }
  }, []);

  // isImageFileType and buildGalleryDocuments are now provided by useProjectDocuments hook

  // Helper function to render file preview
  const renderFilePreview = (
    url: string,
    index: number,
    fileType: string,
    metadata?: any
  ) => {
    // First try to determine if it's an image from metadata
    let isImage = false;

    if (metadata && metadata.fileType) {
      isImage = isImageFileType(metadata.fileType);
    }

    // If metadata didn't confirm it's an image, check the URL
    if (!isImage && url) {
      // Check if URL has image extension
      isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url.toLowerCase());

      // Also check if URL contains image indicators (for Supabase URLs that might not have extensions)
      if (!isImage && metadata && metadata.fileName) {
        isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(
          metadata.fileName.toLowerCase()
        );
      }
    }

    const fileName = metadata?.fileName || `File ${index + 1}`;

    // Format the tooltip with filename and upload date
    let tooltipText = fileName;
    if (metadata?.uploadedAt) {
      try {
        const uploadDate = new Date(metadata.uploadedAt);
        const formattedDate = uploadDate.toLocaleDateString() + ' ' + uploadDate.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
        tooltipText = `${fileName}\nUploaded: ${formattedDate}`;
      } catch (e) {
        // If date parsing fails, just show filename
        tooltipText = fileName;
      }
    }

    const handleImageClick = () => {
      // Build gallery documents only for this specific field
      const fieldDocs = buildGalleryDocuments(fileType);
      const clickedDocIndex = fieldDocs.findIndex(
        (doc) => doc.url === url && doc.index === index
      );

      if (clickedDocIndex !== -1) {
        setGalleryState({
          isOpen: true,
          documents: fieldDocs,
          initialIndex: clickedDocIndex,
        });
      }
    };

    return (
      <div className="w-full h-full" title={tooltipText}>
        {isImage ? (
          <img
            src={url}
            alt="Preview"
            className="w-full h-full object-cover cursor-pointer"
            onClick={handleImageClick}
            onError={(e) => {
              // If image fails to load, show clickable file icon instead
              const target = e.target as HTMLImageElement;
              target.style.display = "none";
              const parent = target.parentElement;
              if (parent) {
                parent.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-muted cursor-pointer" onclick="window.open('${url}', '_blank', 'noopener,noreferrer')"><svg class="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg></div>`;
              }
            }}
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center bg-muted cursor-pointer"
            onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
          >
            <FileText className="h-8 w-8 text-primary" />
          </div>
        )}
      </div>
    );
  };

  // fetchProjectDocuments is now provided by useProjectDocuments hook

  // Fetch document counts when modal opens (fast operation for lazy loading)
  useEffect(() => {
    if (isOpen && projectId) {
      // Fetch counts immediately (fast - no signed URLs)
      fetchDocumentCounts(projectId);
    }
  }, [isOpen, projectId, fetchDocumentCounts]);

  // Call fetchProjectDocuments when the design tab is opened (legacy full load)
  useEffect(() => {
    if (isOpen && projectId && activeTab === "design") {
      // Add a small delay to let the modal render first
      const timeoutId = setTimeout(() => {
        fetchProjectDocuments(projectId);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen, projectId, activeTab, fetchProjectDocuments]);

  // Fetch project details when modal opens or projectId changes
  // (Moved after function definitions)

  // Handle all select/dropdown value changes
  const handleSelectChange = (name: string, value: string) => {
    setFormValues((prev) => {
      const newValues = { ...prev, [name]: value };
      // Check if values have changed from original
      setHasUnsavedChanges(JSON.stringify(newValues) !== JSON.stringify(originalFormValues));
      return newValues;
    });
  };

  // Handle array value changes (for checkboxes, multi-select, etc.)
  const handleArrayChange = (name: string, value: any) => {
    setFormValues((prev) => {
      const newValues = { ...prev, [name]: value };
      // Check if values have changed from original
      setHasUnsavedChanges(JSON.stringify(newValues) !== JSON.stringify(originalFormValues));
      return newValues;
    });
  };

  // handleInputChange and handleSaveOverviewChanges are now provided by useProjectForm hook
  // The hook handles tab-switching via onBeforeSave and recentlySaved via onAfterSave callbacks

  const handleSubmit = useCallback(async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setSaveSuccess(false);

      // 1. Prepare all fields for update (except file fields)
      const fileFields = [
        "power_bill_file",
        "satellite_image_file",
        "site_photos_files",
        "deal_structure_docs_file",
        "design_image_file",
        "drivers_license_file",
        "approved_work_order_file",
        "design_notes",
        "expedited_design",
        "grant_documentation_file",
        "site_plan_file",
        "other_documents_files",
      ];
      const updates = { ...formValues };
      fileFields.forEach((f) => delete updates[f]);

      // 2. Explicitly remove joined fields that are not table columns
      const invalidFields = [
        "biz_dev_rep",
        "sales_rep",
        "internal_pm",
        "installer_partner",
        "owner",
        "closer",
        "setter",
        "creator",
        "owner_ui",
        "sales_rep_ui",
        "internal_pm_ui",
        "installer_partner_ui",
        "biz_dev_rep_ui",
        "stages",
      ];
      invalidFields.forEach((f) => delete updates[f]);

      // 3. Add required update fields
      updates.updated_at = new Date().toISOString();
      updates.last_updated_by_user = user?.id || null;

      // 4. Update the project
      const { error: updateError } = await supabase
        .from("projects")
        .update(updates)
        .eq("id", projectId);
      if (updateError) {
        console.error("Error updating project:", updateError);
        toast({
          title: "Error",
          description: "Failed to update project details.",
          variant: "destructive",
        });
        throw updateError;
      }

      // --- CONTACT LOGIC ---
      // Log full formValues before contact logic
      const contactEmail = formValues.contact_email;
      const contactPhone = formValues.contact_phone;
      let contactId: string | null = null;
      if (contactEmail && contactEmail.trim() !== "") {
        // Log lookup
        const { data: existingContacts, error: lookupError } = await supabase
          .from("contacts")
          .select("id")
          .eq("email", contactEmail)
          .limit(1);
        if (lookupError) {
          console.error(
            "[handleSubmit] Error looking up contact:",
            lookupError.message,
            lookupError.details
          );
          toast({
            title: "Error",
            description: "Failed to check contacts.",
            variant: "destructive",
          });
        }
        if (existingContacts && existingContacts.length > 0) {
          contactId = existingContacts[0].id;
          // Update contact
          const contactData = {
            first_name: formValues.first_name,
            last_name: formValues.last_name,
            email: contactEmail,
            phone: contactPhone || null,
            company_name: formValues.company_name || null,
            updated_at: new Date().toISOString(),
          };
          const { data: updateContactData, error: updateContactError } =
            await supabase
              .from("contacts")
              .update(contactData)
              .eq("id", contactId);
          if (updateContactError) {
            console.error(
              "[handleSubmit] Error updating contact:",
              updateContactError.message,
              updateContactError.details
            );
            toast({
              title: "Error",
              description: "Failed to update contact.",
              variant: "destructive",
            });
          }
        } else {
          // Insert new contact
          const contactData = {
            first_name: formValues.first_name,
            last_name: formValues.last_name,
            email: contactEmail,
            phone: contactPhone || null,
            company_name: formValues.company_name || null,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            tenant_id: await getCurrentTenantId(),
          };
          const { data: newContact, error: contactError } = await supabase
            .from("contacts")
            .insert(contactData)
            .select("id")
            .single();
          if (contactError) {
            console.error(
              "[handleSubmit] Error creating contact:",
              contactError.message,
              contactError.details
            );
            toast({
              title: "Error",
              description: "Failed to create contact.",
              variant: "destructive",
            });
          } else if (newContact?.id) {
            contactId = newContact.id;
          }
        }
      } else {
      }

      // --- PROJECT_CONTACTS LOGIC ---
      if (contactId) {
        const { data: existingProjectContact, error: pcError } = await supabase
          .from("project_contacts")
          .select("*") // Changed from 'id' to '*' to avoid column error
          .eq("project_id", projectId)
          .eq("contact_id", contactId)
          .limit(1);
        if (pcError) {
          console.error(
            "[handleSubmit] Error looking up project_contact:",
            pcError.message,
            pcError.details
          );
          toast({
            title: "Warning",
            description: "Failed to check project contact association.",
            variant: "default",
          });
        } else if (
          !existingProjectContact ||
          existingProjectContact.length === 0
        ) {
          // Create association only if it doesn't exist
          const assocData = {
            project_id: projectId,
            contact_id: contactId,
            role_in_project: "primary",
            is_primary: true,
            created_at: new Date().toISOString(),
            tenant_id: await getCurrentTenantId(),
          };
          const { data: assocResp, error: assocError } = await supabase
            .from("project_contacts")
            .insert(assocData);
          if (assocError) {
            if (assocError.code === "23505") {
              // Duplicate key violation
              console.warn(
                "[handleSubmit] Association already exists, skipping insert:",
                assocError.message
              );
              toast({
                title: "Warning",
                description: "Contact association already exists.",
                variant: "default",
              });
            } else {
              console.error(
                "[handleSubmit] Error creating project_contact:",
                assocError.message,
                assocError.details
              );
              toast({
                title: "Error",
                description: "Failed to associate contact.",
                variant: "destructive",
              });
            }
          }
        } else {
        }
      }

      // 5. Log changes to activity_logs table (optional, keep as is)
      await logChangesToActivityLog(updates);

      // 6. Show success and refresh project (don't close modal)
      setSaveSuccess(true);
      await fetchProject();
      toast({
        title: "Success",
        description: "Project saved successfully!",
      });
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      toast({
        title: "Error",
        description: "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  }, [formValues, projectId, user]);

  // handleDrag, handleRemoveFile, handleFileChange, handleDrop, handleVerifyDriversLicense,
  // handleVerifyUtilityBill are now provided by useProjectDocuments hook

  const handleEntityTypeChange = (value: string) => {
    setFormValues((prev) => ({
      ...prev,
      entity_type: value,
    }));
  };

  // Handle tab change
  const handleTabChange = (tab: string) => {
    // Prevent switching back to new-project-form after we've intentionally left it
    if (tab === 'new-project-form' && hasLeftFormTab.current) {
      return;
    }

    setActiveTab(tab);
    setTabLoaded((prev) => ({ ...prev, [tab]: true }));
    setIsManualTabChange(true);
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set("tab", tab);
    window.history.replaceState({}, "", newUrl);
  };

  const handleClose = () => {
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.delete("tab");
    window.history.replaceState({}, "", newUrl);
    setIsManualTabChange(false);

    // Close modal immediately - let SlidePanel handle the animation
    onClose();

    // Reset content state after animation is complete
    setTimeout(() => {
      setContentLoaded(false);
    }, 400); // Wait for slide animation to complete
  };

  const handleDelete = async () => {
    try {
      // ⚠️ MUST stay an RPC. A direct `.update({ deleted_at })` is refused by
      // RLS for everyone except a superadmin: the row it writes stops passing
      // `projects_select` (`deleted_at IS NULL`), and Postgres will not let a
      // statement write a row out of its own view. It fails with "new row
      // violates row-level security policy", so the button reported success
      // nowhere and deleted nothing. `soft_delete_project()` is SECURITY
      // DEFINER and authorizes the caller itself.
      await projectService.deleteProject(projectId);

      toast({
        title: "Project Deleted",
        description: "The project has been successfully deleted.",
      });

      // Call the parent's onDelete if provided
      if (onDelete) {
        onDelete();
      }

      // Close the modal
      handleClose();
    } catch (error) {
      console.error("Error deleting project:", error);
      toast({
        title: "Delete Failed",
        description:
          error instanceof Error
            ? error.message
            : "An error occurred while deleting the project",
        variant: "destructive",
      });
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const completedTask = await taskService.completeTask(taskId);
      // Remove the completed task from openTasks and add to completedTasks
      setOpenTasks(openTasks.filter((task) => task.id !== taskId));
      setCompletedTasks([completedTask, ...completedTasks]);
      setSelectedTask(null);
    } catch (error) {
      console.error("Error completing task:", error);
      toast({
        title: "Error",
        description: "Failed to complete task",
        variant: "destructive",
      });
    }
  };

  const handleUncompleteTask = async (taskId: string) => {
    try {
      const uncompletedTask = await taskService.uncompleteTask(taskId);
      // Remove from completedTasks and add back to openTasks
      setCompletedTasks(completedTasks.filter((task) => task.id !== taskId));
      setOpenTasks([...openTasks, uncompletedTask]);
      setSelectedTask(null);
    } catch (error) {
      console.error("Error uncompleting task:", error);
      toast({
        title: "Error",
        description: "Failed to uncomplete task",
        variant: "destructive",
      });
    }
  };

  // Redirect non-admin users from partner tab if they somehow got there
  useEffect(() => {
    // If user doesn't have partner permissions and they're on the partner tab, redirect them to opportunity tab
    if (
      activeTab === "partner" &&
      !(
        hasPermission("manage_partners") ||
        hasPermission("view_partners") ||
        hasPermission("edit_partners") ||
        hasPermission("project.manage_all") ||
        hasPermission("system.override") ||
        hasPermission("tenant.manage")
      )
    ) {
      handleTabChange("opportunity");
    }
  }, [activeTab, hasPermission]);

  // Fetch the site survey completion date from checklist items
  const fetchSiteSurveyCompletionDate = async (projectId: string) => {
    try {
      // First, find the site survey checklist template
      const { data: templates, error: templatesError } = await supabase
        .from("checklist_templates")
        .select("id, name")
        .ilike("name", "%site survey%");

      if (templatesError) throw templatesError;

      if (!templates || templates.length === 0) {
        return;
      }

      // Get template IDs
      const templateIds = templates.map((t) => t.id);

      // Find the "Completed Date" checklist item for site survey templates
      const { data: completedDateItems, error: itemsError } = await supabase
        .from("checklist_items")
        .select("id, template_id, item_name")
        .in("template_id", templateIds)
        .ilike("item_name", "%completed date%");

      if (itemsError) throw itemsError;

      if (!completedDateItems || completedDateItems.length === 0) {
        return;
      }

      // Get the IDs of the "Completed Date" items
      const completedDateItemIds = completedDateItems.map((item) => item.id);

      // Find project checklist items that match these template items
      const { data: projectItems, error: projectItemsError } = await supabase
        .from("project_checklist_items")
        .select("id, template_item_id, is_completed, completed_at, value_date")
        .eq("project_id", projectId)
        .in("template_item_id", completedDateItemIds)
        .eq("is_completed", true);

      if (projectItemsError) throw projectItemsError;

      if (!projectItems || projectItems.length === 0) {
        return;
      }

      // Find the completed site survey date item
      // Prefer value_date if available, otherwise use completed_at
      const completedItem = projectItems[0]; // Take the first matching item
      const completionDate =
        completedItem.value_date || completedItem.completed_at;

      if (completionDate) {
        setSiteSurveyCompletionDate(completionDate);

        // Format the date as YYYY-MM-DD for the input field
        const formattedDate = new Date(completionDate)
          .toISOString()
          .split("T")[0];

        // Update the form values with the completion date
        setFormValues((prev) => ({
          ...prev,
          site_survey_completion_date: formattedDate,
        }));
      } else {
      }
    } catch (error) {
      console.error("Error fetching site survey completion date:", error);
    }
  };

  // Fetch project details when modal opens or projectId/tab changes
  useEffect(() => {
    let isMounted = true;

    const loadData = async () => {
      if (!projectId || !isOpen) return;

      try {
        // Always fetch project data
        await fetchProject();

        // If finance tab is active, fetch additional data
        if (activeTab === "finance" && isMounted) {
          try {
            await fetchSiteSurveyCompletionDate(projectId);
          } catch (err) {
            console.error("Error fetching site survey completion date:", err);
            // Continue even if this fails
          }

          try {
            if (isMounted) {
              await fetchApprovedWorkOrderFile(projectId);
            }
          } catch (err) {
            console.error("Error fetching approved work order file:", err);
            // Continue even if this fails
          }
        }
      } catch (err) {
        console.error("Error loading project data:", err);
        // Ensure we're not stuck in loading state
        if (isMounted) {
          setLoading(false);
        }
      } finally {
        // Set loading to false after all data is fetched
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    // Cleanup function to prevent state updates after unmount
    return () => {
      isMounted = false;
    };
  }, [projectId, isOpen, activeTab, project?.pipeline_id]); // Remove function dependencies to prevent infinite loops

  // --- FOLLOWER UTILS ---
  function getInitials(user: any) {
    if (!user) return "?";
    const first = user.first_name || "";
    const last = user.last_name || "";
    return (first[0] || "").toUpperCase() + (last[0] || "").toUpperCase();
  }

  // Note: addFollower, removeFollower, and all handleChange* functions
  // are now provided by useProjectAssignments hook

  const handleProjectUpdate = (updatedProject: any) => {
    // Set the recentlySaved flag to prevent fetchProject from overwriting this update
    setRecentlySaved(true);

    // Clear any existing timeout to prevent multiple timers
    if (recentlySavedTimeoutRef.current) {
      clearTimeout(recentlySavedTimeoutRef.current);
    }

    // Set new timeout to clear the flag after 5 seconds (increased from 3)
    recentlySavedTimeoutRef.current = setTimeout(() => {
      setRecentlySaved(false);
      recentlySavedTimeoutRef.current = null;
    }, 5000);

    // Update both the formValues and project state to ensure UI reflects changes
    setFormValues((prev) => ({
      ...prev,
      ...updatedProject,
    }));

    // Update the project state to reflect changes immediately in the UI
    if (project) {
      setProject({
        ...project,
        ...updatedProject,
      });
    }

    // Refresh followers list when project is updated (in case new followers were added)
    if (projectId) {
      // Add a small delay to ensure database updates have completed
      setTimeout(() => {
        fetchFollowers();
      }, 500);
    }

    // Trigger refresh of ProjectManagementTab to update reference fields
    setProjectManagementRefreshKey((prev) => {
      const newKey = prev + 1;
      return newKey;
    });
  };

  // handleStatusChange is now managed by useProjectStateManagement hook

  // fetchStages is now managed by useProjectStateManagement hook

  // Modal open effect - Fixed infinite loop by removing hasOpened dependency
  useEffect(() => {
    if (isOpen && projectId && !hasOpened) {
      // Set hasOpened flag
      setHasOpened(true);

      // Show skeleton immediately when modal opens - no delay
      setContentLoaded(false);

      // Start loading content immediately
      fetchProject().then(() => {
        // Smooth transition from skeleton to content
        setTimeout(() => {
          setContentLoaded(true);
        }, 200);
      });
    }
  }, [isOpen, projectId]); // Removed hasOpened from dependencies to prevent infinite loop

  // useEffect for fetching stages is now in useProjectStateManagement hook

  // handleStageChange is now managed by useProjectStateManagement hook

  // Close status dropdown when clicking outside is now in useProjectStateManagement hook

  // Initialize tab from URL when modal opens - removed project dependency to run immediately
  useEffect(() => {
    // Only run once when modal first opens
    if (isOpen && !hasSetInitialTab.current && !isManualTabChange) {
      hasSetInitialTab.current = true;

      // Capture initial values in refs to prevent re-running when they change
      initialFormCompleted.current = isFormCompleted;
      initialPipelineId.current = formValues.pipeline_id || null;

      // Clear any existing tab parameter to avoid conflicts
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete("tab");
      window.history.replaceState({}, "", newUrl);

      // PRIORITY 1: defaultTab if provided (from notification clicks, etc.)
      if (defaultTab) {
        setActiveTab(defaultTab);
        setTabLoaded((prev) => ({ ...prev, [defaultTab]: true }));
        newUrl.searchParams.set("tab", defaultTab);
        window.history.replaceState({}, "", newUrl);
      }
      // PRIORITY 2: new-project-form if needed
      else if (
        !isFormCompleted &&
        [1, 57].includes(formValues.pipeline_id || 0)
      ) {
        setActiveTab("new-project-form");
        setTabLoaded((prev) => ({ ...prev, "new-project-form": true }));
        newUrl.searchParams.set("tab", "new-project-form");
        window.history.replaceState({}, "", newUrl);
      }
      // PRIORITY 4: Default to opportunity
      else {
        setActiveTab("opportunity");
        setTabLoaded((prev) => ({ ...prev, opportunity: true }));
        newUrl.searchParams.delete("tab"); // Ensure tab is cleared
        window.history.replaceState({}, "", newUrl);
      }
      setIsManualTabChange(false); // Reset manual flag after initial tab set
    }
  }, [isOpen, defaultTab]);

  // Reset the flags when modal closes
  useEffect(() => {
    if (!isOpen) {
      hasSetInitialTab.current = false;
      isFetchingProject.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    // Don't auto-switch if user has intentionally left the form tab via save
    if (hasLeftFormTab.current) {
      return;
    }

    if (
      activeTab === "new-project-form" &&
      (isFormCompleted || ![1, 57].includes(formValues.pipeline_id || 0))
    ) {
      hasLeftFormTab.current = true; // Mark that we're leaving the form
      setActiveTab("opportunity");
      setTabLoaded((prev) => ({ ...prev, opportunity: true }));
    }
  }, [activeTab, isFormCompleted, formValues.pipeline_id]);

  // Reset the form tab ref when modal closes or project changes
  useEffect(() => {
    if (!isOpen) {
      hasLeftFormTab.current = false;
    }
  }, [isOpen]);

  useEffect(() => {
    hasLeftFormTab.current = false;
  }, [projectId]);

  // Don't render anything if modal is not open
  if (!isOpen) return null;

  const handleTitleSave = async () => {
    if (!project || !editableTitle.trim()) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from("projects")
        .update({
          project_name: editableTitle.trim(),
          updated_at: new Date().toISOString(),
          last_updated_by_user: user?.id,
        })
        .eq("id", project.id);

      if (error) throw error;

      // Update local state
      setProject((prev) =>
        prev ? { ...prev, project_name: editableTitle.trim() } : null
      );
      setIsEditingTitle(false);

      // Log the change
      await logChangesToActivityLog({
        project_name: editableTitle.trim(),
      });

      toast({
        title: "Success",
        description: "Project title updated successfully",
      });
    } catch (error) {
      console.error("Error updating project title:", error);
      toast({
        title: "Error",
        description: "Failed to update project title",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Guard: If projectId is not defined, do not render or fetch
  if (!projectId) {
    return null;
  }

  // Update dropdown position when opened is now in useProjectStateManagement hook

  // Real-time subscription is now in useProjectData hook

  const handleCreateTask = async (task: CreateTaskInput) => {
    try {
      const newTask = await taskService.createTask({
        ...task,
        project_id: projectId,
      });
      setOpenTasks([newTask, ...openTasks]);
      setShowTaskForm(false);
    } catch (error) {
      console.error("Error creating task:", error);
      toast({
        title: "Error",
        description: "Failed to create task",
        variant: "destructive",
      });
    }
  };

  const handleTestStageChange = async () => {
    const pipelineUrl = "/app/pipeline-resi";
    const notificationLink = `${pipelineUrl}?projectId=${project?.id || projectId}&modal=true`;

    await notifyProjectFollowers({
      projectId: project?.id || projectId,
      type: "project_stage_move",
      projectName: project?.project_name || "Smith Family Solar Project",
      projectType: project?.project_type || "Residential",
      senderId: "cad5ff05-b71d-47df-aa93-f141ca1a7e17",
      senderName: "Mike Chen",
      message:
        'Project has moved from "Electrical Inspection" to "System Activation".',
      link: notificationLink,
      metadata: {
        old_stage: "Electrical Inspection",
        new_stage: "System Activation",
        test: true,
        project_id: project?.id || projectId,
        customer_name: "Robert Smith",
      },
      isInternalComment: false,
    });
    alert("Test project stage change notification sent!");
  };

  const handleTestProjectComment = async () => {
    const pipelineUrl = "/app/pipeline-resi";
    const notificationLink = `${pipelineUrl}?projectId=${project?.id || projectId}&modal=true&tab=communication`;

    await notifyProjectFollowers({
      projectId: project?.id || projectId,
      type: "project_comment",
      projectName: project?.project_name || "Smith Family Solar Project",
      projectType: project?.project_type || "Residential",
      senderId: "cad5ff05-b71d-47df-aa93-f141ca1a7e17",
      senderName: "Mike Chen",
      message:
        "The electrical inspection has been completed successfully. All systems are ready for the next phase.",
      link: notificationLink,
      metadata: {
        message_id: `test-${Date.now()}`,
        test: true,
        project_id: project?.id || projectId,
        comment_text:
          "The electrical inspection has been completed successfully. All systems are ready for the next phase.",
        customer_name: "Robert Smith",
      },
      isInternalComment: true,
    });
    alert("Test project comment notification sent!");
  };

  // SlidePanel manages its own visibility and exit animation
  // Don't return null here - let SlidePanel handle the animation

  return (
    <>
      <SlidePanel
        isOpen={isOpen}
        onClose={handleClose}
        width="xl"
        position="right"
        showBackdrop={true}
        backdropClassName="bg-transparent backdrop-blur-none"
        topOffset="calc(4rem + 5px)"
        // `project-modal-panel` is a STABLE HOOK, not styling. styles/mobile-viewport-fix-v2.css
        // targeted `.md\:left-\[244px\]` — a Tailwind class name used as a CSS selector — so the
        // width below could not be changed without silently breaking that mobile height override.
        // The hook decouples the two; keep it even if these classes change again.
        //
        // The sidebar sizes to its longest label (NAV-2.4) and publishes its resting width as
        // --app-sidebar-w, which MainLayout already offsets the page by. This panel was still
        // pinned to the old fixed 244px, so once the rail grew past that the panel overlapped it,
        // and when the rail was COLLAPSED to 3.5rem the panel left 188px of dead space. Following
        // the variable fixes both directions at once and needs no collapse state of its own.
        //
        // Desktop only. Below md the rail is an off-canvas drawer, but it still measures its real
        // width, so keying mobile off the variable would indent the panel by a sidebar that is not
        // on screen. Mobile keeps its 76px.
        //
        // 244px stays as the var() fallback: the variable is published by an effect in Sidebar, so
        // there is a first paint before it exists, and any surface rendered without the sidebar
        // mounted never gets it at all.
        panelClassName="project-modal-panel left-[76px] md:left-[var(--app-sidebar-w,244px)] w-[calc(100vw-76px)] md:w-[calc(100vw_-_var(--app-sidebar-w,244px))] border-l-2 border-t-2 border-border rounded-tl-lg bottom-0 h-[calc(100vh-4rem-5px)] shadow-[0_35px_80px_-15px_rgba(0,0,0,0.4)]"
        contentClassName="!p-0"
        showCloseButton={false}
        closeOnBackdropClick={true}
        showEdgeCloseButton={true}
      >
        <DrawerStackProvider>
        {/* Show skeleton while content is loading */}
        {!contentLoaded ? (
          <ProjectModalSkeleton />
        ) : (
          <>
            <div className="flex flex-col h-full overflow-y-auto" style={{ overflowX: 'clip' }}>
              {/* SUB-4.4 · FR38, design A1.
                  ⚠️ THE STICKINESS MOVED UP A LEVEL RATHER THAN BEING DUPLICATED.
                  The title row below used to carry `sticky top-0` itself. Two
                  siblings both pinned to top-0 do not stack; the second slides
                  under the first, so the account bar would have covered the
                  project name on scroll. One sticky wrapper holding both keeps
                  the existing behaviour exactly and puts the attribution above
                  it, which is what "persistent" in FR38 means once the record is
                  taller than the viewport. */}
              <div className="sticky top-0 z-[170] bg-background flex-shrink-0">
                <RecordAccountBar
                  state={recordAccountBarState(
                    project?.tenant_id,
                    mergedScope,
                    accountNames,
                  )}
                />
              {/* Header */}
              <div className="bg-background flex flex-col lg:flex-row rounded-t-lg overflow-hidden flex-shrink-0 px-6 pt-3">
                {/* Project title and address section */}
                <div className="bg-background pl-0 pr-4 pt-2 pb-0 md:pl-0 md:pr-6 md:pt-2 md:pb-0 lg:pb-2 md:border-b-0 lg:border-r-0 lg:border-b border-border shadow-sm lg:flex-1 lg:min-w-0 flex items-center">
                  <div className="flex flex-col sm:flex-row lg:flex-col space-y-1 sm:space-y-0 lg:space-y-1 w-full pl-12">
                    {/* Project Title */}
                    <h2 className="text-lg sm:text-xl font-semibold text-foreground truncate flex items-center">
                      {isEditingTitle ? (
                        <div className="flex items-center space-x-2 w-full">
                          <Input
                            type="text"
                            value={editableTitle}
                            onChange={(e) => setEditableTitle(e.target.value)}
                            className="text-lg sm:text-xl font-semibold"
                            autoFocus
                          />
                          <button
                            onClick={handleTitleSave}
                            className="p-1 text-success hover:text-success"
                            disabled={saving}
                          >
                            <Check className="h-5 w-5" />
                          </button>
                          <button
                            onClick={() => {
                              setIsEditingTitle(false);
                              setEditableTitle(project?.project_name || "");
                            }}
                            className="p-1 text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-5 w-5" />
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate flex-1 min-w-0 cursor-help">
                                {project?.project_name || ""}
                              </span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p className="max-w-sm break-words">
                                {project?.project_name || ""}
                              </p>
                            </TooltipContent>
                          </Tooltip>
                          <div className="flex items-center space-x-2 flex-shrink-0">
                            {hasManagementPermissions() && (
                              <button
                                onClick={() => {
                                  setIsEditingTitle(true);
                                  setEditableTitle(project?.project_name || "");
                                }}
                                className="p-1 text-muted-foreground hover:text-foreground flex-shrink-0"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            )}
                            {/* Share Button */}
                            {projectId && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <button
                                    onClick={async () => {
                                      try {
                                        // Build the link on the tenant's live
                                        // domain — the same approach notification
                                        // links use (the origin you're served
                                        // from IS the tenant domain). Falls back
                                        // to the canonical app host. We no longer
                                        // read VITE_APP_URL: it was baked to a
                                        // retired domain (go.solerro.com) in prod.
                                        const baseUrl =
                                          typeof window !== 'undefined' && window.location.origin
                                            ? window.location.origin
                                            : 'https://app.joulescrm.com';

                                        // Clean deep-link that opens the project
                                        // modal (see /app/projects/:projectId).
                                        const url = `${baseUrl}/app/projects/${projectId}`;
                                        await navigator.clipboard.writeText(
                                          url
                                        );
                                        toast({
                                          title: "✅ Project Link Copied!",
                                          description:
                                            "Project link has been copied to your clipboard.",
                                          duration: 3000,
                                        });
                                      } catch (error) {
                                        console.error(
                                          "Failed to copy to clipboard:",
                                          error
                                        );
                                        toast({
                                          title: "❌ Copy Failed",
                                          description:
                                            "Failed to copy project link to clipboard.",
                                          duration: 3000,
                                          variant: "destructive",
                                        });
                                      }
                                    }}
                                    className="p-1 text-muted-foreground hover:text-foreground flex-shrink-0"
                                    aria-label="Copy project link"
                                    type="button"
                                  >
                                    <Share2 className="h-4 w-4" />
                                  </button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  Copy project link
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {/* SCH-4.3: Sync Now button — only on SCH-imported
                                projects, superadmin only at V1 (mirrors the
                                backfill endpoints' gate). Dispatches a single-
                                project POC sync via the existing start-backfill
                                endpoint with auto_continue=true so the run
                                completes in one shot (~2s via SCH-3.6's fast
                                fetch RPC). */}
                            {project?.external_source === 'subcontractorhub' &&
                              project?.external_id &&
                              canRunSchSync && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button
                                      onClick={() => {
                                        if (syncNowMutation.isPending) return;
                                        const externalId = project.external_id;
                                        if (!externalId) return;
                                        syncNowMutation.mutate(
                                          {
                                            batchSize: 1,
                                            subhubUuidFilter: [externalId],
                                            autoContinue: true,
                                          },
                                          {
                                            onSuccess: (result) => {
                                              if (result.error) {
                                                toast({
                                                  title: 'Sync Now failed',
                                                  description: result.error.message,
                                                  variant: 'destructive',
                                                });
                                                return;
                                              }
                                              toast({
                                                title: 'Sync started',
                                                description:
                                                  "Fetching this project from SubContractorHub. The project will update automatically when the sync completes.",
                                              });
                                            },
                                            onError: (err) => {
                                              toast({
                                                title: 'Sync Now failed',
                                                description:
                                                  err instanceof Error
                                                    ? err.message
                                                    : 'Unknown error',
                                                variant: 'destructive',
                                              });
                                            },
                                          },
                                        );
                                      }}
                                      disabled={syncNowMutation.isPending}
                                      className="p-1 text-muted-foreground hover:text-foreground flex-shrink-0 disabled:opacity-50 disabled:cursor-not-allowed"
                                      aria-label="Sync this project from SubContractorHub"
                                      type="button"
                                    >
                                      {syncNowMutation.isPending ? (
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                      ) : (
                                        <RefreshCw className="h-4 w-4" />
                                      )}
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    Sync now from SubContractorHub
                                  </TooltipContent>
                                </Tooltip>
                              )}
                          </div>
                        </div>
                      )}
                    </h2>

                    {project?.project_street && (
                      <div className="flex items-center sm:ml-4 lg:ml-0 text-muted-foreground min-w-0">
                        {/* Divider only visible in tablet view */}
                        <span className="hidden sm:inline lg:hidden mx-2">
                          |
                        </span>
                        <MapPin className="h-4 w-4 mr-1 flex-shrink-0 text-muted-foreground" />
                        <span className="text-sm sm:text-base truncate max-w-[200px] sm:max-w-[350px] lg:max-w-[550px]">
                          {project.project_street}
                          {project.project_city
                            ? `, ${project.project_city}`
                            : ""}
                          {project.project_state
                            ? `, ${project.project_state}`
                            : ""}
                          {project.project_zip_code
                            ? ` ${project.project_zip_code}`
                            : ""}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-background border-b border-border px-4 py-2 md:px-6 md:pr-[100px] md:py-2 flex items-center justify-end md:w-auto md:border-l-0 -mx-6">
                  <div className="flex flex-wrap gap-2 justify-end">
                    {/* Stage Badge */}
                    <div
                      className="inline-flex flex-col items-center px-2 pt-1 pb-0.5 rounded-md text-xs font-medium bg-muted text-foreground border border-border cursor-pointer hover:bg-muted/80 transition"
                      // PMV-1.5/1.6. Not hasManagementPermissions(): that admits
                      // edit_projects, which every role holds, so reps moved here
                      // directly. Anyone else gets the same picker as a request,
                      // like the board's Request move.
                      // RBAC-3.93: a stage request is a `tasks` row, refused to a partner too.
                      onClick={() => canWrite && setShowStageDialog(true)}
                      title={
                        !canWrite
                          ? undefined
                          : canMoveStageDirectly
                          ? "Click to change stage"
                          : "Click to request a stage change"
                      }
                    >
                      <div className="flex items-center justify-center mb-0.5">
                        <Clock className="h-4 w-4 mr-1 text-primary" />
                        <span className="text-sm text-muted-foreground font-medium">
                          Stage
                        </span>
                      </div>
                      <span className="relative font-medium text-center min-h-[19px] flex items-center justify-center px-1 whitespace-nowrap">
                        {project?.stages?.name || "Not Assigned"}
                      </span>
                    </div>

                    {/* Status Badge */}
                    <div
                      className={`inline-flex flex-col items-center px-2 pt-1 pb-0.5 rounded-md text-xs font-medium bg-muted text-foreground border border-border ${hasManagementPermissions() ? "cursor-pointer hover:bg-muted/80 transition" : ""}`}
                      onClick={() => {
                        if (hasManagementPermissions())
                          setShowStatusDialog(true);
                      }}
                      title={
                        hasManagementPermissions()
                          ? "Click to change status"
                          : ""
                      }
                    >
                      <div className="flex items-center justify-center mb-0.5">
                        <FileCheck className="h-4 w-4 mr-1 text-primary" />
                        <span className="text-sm text-muted-foreground font-medium">
                          Status
                        </span>
                      </div>
                      <span className="relative font-medium text-center min-h-[19px] flex items-center justify-center px-1 whitespace-nowrap">
                        {project?.project_status || "Active"}
                      </span>
                    </div>

                    {/* Owner Badge */}
                    {project?.assigned_sales_rep_id && (
                      <div
                        className={`inline-flex flex-col items-center px-2 pt-1 pb-0.5 rounded-md text-xs font-medium bg-muted text-foreground border border-border ${hasManagementPermissions() ? "cursor-pointer hover:bg-muted/80 transition" : ""}`}
                        onClick={() => {
                          if (hasManagementPermissions())
                            setShowChangeOwner(true);
                        }}
                        title={
                          hasManagementPermissions()
                            ? "Click to change owner"
                            : ""
                        }
                      >
                        <div className="flex items-center justify-center mb-0.5">
                          <User className="h-4 w-4 mr-1 text-primary" />
                          <span className="text-sm text-muted-foreground font-medium">
                            Owner
                          </span>
                        </div>
                        <span className="font-medium text-center min-h-[19px] flex items-center justify-center px-1 whitespace-nowrap">
                          {(() => {
                            if (project?.owner) {
                              return `${project.owner.first_name} ${project.owner.last_name}`;
                            }
                            if (project?.sales_rep) {
                              const firstName =
                                project.sales_rep.first_name || "";
                              const lastName =
                                project.sales_rep.last_name || "";
                              const fullName =
                                `${firstName} ${lastName}`.trim();
                              return fullName || "Unknown";
                            }
                            return "Unknown";
                          })()}
                        </span>
                      </div>
                    )}

                    {/* Followers Badge */}
                    {project?.assigned_sales_rep_id && (
                      <div
                        className={`inline-flex flex-col items-center px-2 pt-1 pb-0.5 rounded-md text-xs font-medium bg-muted text-foreground border border-border ${hasManagementPermissions() ? "cursor-pointer hover:bg-muted/80 transition" : ""}`}
                        onClick={() => {
                          if (hasManagementPermissions())
                            setShowAddFollower(true);
                        }}
                        title={
                          hasManagementPermissions()
                            ? "Click to manage followers"
                            : ""
                        }
                      >
                        <div className="flex items-center justify-center mb-0.5">
                          <Users className="h-4 w-4 mr-1 text-primary" />
                          <span className="text-sm text-muted-foreground font-medium">
                            Followers
                          </span>
                        </div>
                        <span className="font-medium text-center h-[19px] flex items-center justify-center whitespace-nowrap">
                          {followers.length > 0 ? followers.length : "None"}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {/* SUB-4.4 · closes the sticky wrapper that holds the account bar
                  and the title row together. */}
              </div>

              {/* Pipeline Timeline Banner */}
              {stages.length > 0 && project?.current_stage_id && (
                <div className="bg-muted/30 border-b border-border px-2 py-1">
                  <PipelineTimeline
                    currentStageId={project.current_stage_id}
                    stages={stages}
                    stageChangedAt={project.stage_changed_at}
                    stageHistory={stageHistory}
                  />
                </div>
              )}

              {/* RBAC-3.93: say once, at the top, why nothing on this project can be changed. */}
              {partnerScope.scoped && (
                <div
                  className="px-4 py-2 text-sm text-muted-foreground bg-muted/50 border-b border-border"
                  data-testid="partner-read-only-note"
                >
                  {PARTNER_PROJECT_READ_ONLY_NOTE}
                </div>
              )}

              {/* Open Tasks Notification */}
              <div className="flex items-center gap-2 border-b border-primary/20">
                {openTasks.length > 0 && (
                  <button
                    onClick={() => {
                      handleTabChange("project-tasks");
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-primary bg-primary/5 hover:bg-primary/10 transition-colors"
                    style={{
                      transition: "all 300ms ease-in-out",
                      opacity: openTasks.length > 0 ? 1 : 0,
                      transform:
                        openTasks.length > 0
                          ? "translateY(0)"
                          : "translateY(-10px)",
                      height: openTasks.length > 0 ? "auto" : "0",
                      overflow: "hidden",
                    }}
                  >
                    <ClipboardList className="h-4 w-4 mr-2 text-primary" />
                    <span>
                      {openTasks.length} open{" "}
                      {openTasks.length === 1 ? "task" : "tasks"} for this
                      project
                    </span>
                    <ChevronRight className="h-4 w-4 ml-auto text-primary" />
                  </button>
                )}
                {/* Remove the old Add Task button */}
                {/* <button
            onClick={() => {
              handleTabChange("tasks");
              setShowTaskForm(true);
            }}
            className="flex items-center gap-2 px-4 py-2 text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <Plus className="h-4 w-4" />
            <span>Add Task</span>
          </button> */}
              </div>

              {/* Task Dialog */}
              {!!selectedTask && (
                <>
                  <div
                    className="fixed inset-0 z-[300] bg-black/50"
                    onClick={() => {
                      setSelectedTask(null);
                      setEditingTask(false);
                    }}
                  />
                  <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 pointer-events-none">
                    <div className="sm:max-w-[600px] w-full relative bg-background border shadow-lg rounded-lg grid gap-4 p-6 max-h-[90vh] overflow-auto pointer-events-auto">
                      <div className="flex flex-col space-y-1.5 text-center sm:text-left">
                        <div className="flex items-center justify-between">
                          <h2 className="text-lg font-semibold leading-none tracking-tight">
                            {editingTask ? "Edit Task" : "Task Details"}
                          </h2>
                          <button
                            onClick={() => {
                              setSelectedTask(null);
                              setEditingTask(false);
                            }}
                            className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                          >
                            <X className="h-4 w-4" />
                            <span className="sr-only">Close</span>
                          </button>
                        </div>
                      </div>
                  {selectedTask && !editingTask && (
                    <TaskDetails
                      task={selectedTask}
                      onEdit={canWrite ? () => setEditingTask(true) : undefined}
                      onDelete={!canWrite ? undefined : async () => {
                        if (selectedTask) {
                          try {
                            await taskService.deleteTask(selectedTask.id);
                            setOpenTasks(
                              openTasks.filter(
                                (task) => task.id !== selectedTask.id
                              )
                            );
                            setSelectedTask(null);
                            setEditingTask(false);
                            toast({
                              title: "Success",
                              description: "Task deleted successfully",
                            });
                          } catch (error) {
                            console.error("Error deleting task:", error);
                            toast({
                              title: "Error",
                              description: "Failed to delete task",
                              variant: "destructive",
                            });
                          }
                        }
                      }}
                      userId={user?.id || null}
                      onComplete={!canWrite ? undefined : async (id) => {
                        try {
                          const completedTask =
                            await taskService.completeTask(id);
                          // Remove the completed task from openTasks and add to completedTasks
                          setOpenTasks(
                            openTasks.filter((task) => task.id !== id)
                          );
                          setCompletedTasks([completedTask, ...completedTasks]);
                          setSelectedTask(null);
                        } catch (error) {
                          console.error("Error completing task:", error);
                          toast({
                            title: "Error",
                            description: "Failed to complete task",
                            variant: "destructive",
                          });
                        }
                      }}
                      onUncomplete={async (id) => {
                        try {
                          const uncompletedTask =
                            await taskService.uncompleteTask(id);
                          // Remove from completedTasks and add back to openTasks
                          setCompletedTasks(
                            completedTasks.filter((task) => task.id !== id)
                          );
                          setOpenTasks([...openTasks, uncompletedTask]);
                          setSelectedTask(null);
                        } catch (error) {
                          console.error("Error uncompleting task:", error);
                          toast({
                            title: "Error",
                            description: "Failed to uncomplete task",
                            variant: "destructive",
                          });
                        }
                      }}
                      onClose={() => setSelectedTask(null)}
                    />
                  )}
                  {selectedTask && editingTask && (
                    <TaskModal
                      isOpen={editingTask}
                      onClose={() => setEditingTask(false)}
                      task={selectedTask}
                      onSubmit={async (updates) => {
                        try {
                          const updatedTask = await taskService.updateTask(
                            selectedTask.id,
                            updates
                          );
                          // Update the task in openTasks list
                          setOpenTasks(
                            openTasks.map((task) =>
                              task.id === selectedTask.id ? updatedTask : task
                            )
                          );
                          setSelectedTask(updatedTask);
                          setEditingTask(false);
                          toast({
                            title: "Success",
                            description: "Task updated successfully",
                          });
                        } catch (error) {
                          console.error("Error updating task:", error);
                          toast({
                            title: "Error",
                            description: "Failed to update task",
                            variant: "destructive",
                          });
                        }
                      }}
                    />
                  )}
                    </div>
                  </div>
                </>
              )}

              {/* Task Creation Modal */}
              <TaskModal
                isOpen={showTaskForm}
                onClose={() => setShowTaskForm(false)}
                onSubmit={handleCreateTask}
                projectId={projectId}
                title="New Task"
                description="Create a new task for this project"
              />

              {/* Content */}
              {loading ? (
                <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                    <p className="mt-2 text-muted-foreground">
                      Loading project details...
                    </p>
                  </div>
                </div>
              ) : error ? (
                <div className="flex-1 p-8 text-destructive overflow-y-auto">
                  <p>Error: {error}</p>
                </div>
              ) : (
                <div className="flex flex-1 min-h-0" style={{ overflowX: 'clip', overflowY: 'hidden' }}>
                  {/* Sidebar Navigation */}
                  <ProjectModalSidebar
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    project={project}
                    formValues={formValues}
                    isFormCompleted={isFormCompleted}
                    sidebarCollapsed={sidebarCollapsed}
                    onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
                    hasPermission={hasPermission}
                  />

                  {/* Content Area - relative container for contained drawers */}
                  <ModalContentArea activeTab={activeTab}>
                    <ProjectModalTabRenderer
                      activeTab={activeTab}
                      tabLoaded={tabLoaded}
                      project={project}
                      projectId={projectId}
                      formValues={formValues}
                      originalFormValues={originalFormValues}
                      hasUnsavedChanges={hasUnsavedChanges}
                      saving={saving}
                      formErrors={formErrors}
                      sidebarCollapsed={sidebarCollapsed}
                      hasPermission={hasPermission}
                      canEditProject={canEditProject}
                      currentUserId={currentUserId}
                      userRoleId={userRoleId}
                      handleInputChange={handleInputChange}
                      handleSelectChange={handleSelectChange}
                      handleArrayChange={handleArrayChange}
                      setFormValues={setFormValues}
                      setHasUnsavedChanges={setHasUnsavedChanges}
                      handleSaveOverviewChanges={handleSaveOverviewChanges}
                      handleProjectUpdate={handleProjectUpdate}
                      handleSubmit={handleSubmit}
                      updateFormDataFromModal={updateFormDataFromModal}
                      handleFileChange={handleFileChange}
                      handleDrag={handleDrag}
                      handleDrop={handleDrop}
                      handleRemoveFile={handleRemoveFile}
                      handleVerifyDriversLicense={handleVerifyDriversLicense}
                      handleVerifyUtilityBill={handleVerifyUtilityBill}
                      renderFilePreview={renderFilePreview}
                      documents={documents}
                      documentMetadata={documentMetadata}
                      dragActive={dragActive}
                      filePreviewUrls={filePreviewUrls}
                      fileLoading={fileLoading}
                      uploadStatus={uploadStatus}
                      dlVerificationStatus={dlVerificationStatus}
                      dlVerificationData={dlVerificationData}
                      ubVerificationStatus={ubVerificationStatus}
                      ubVerificationData={ubVerificationData}
                      isVerifying={isVerifying}
                      isVerifyingUb={isVerifyingUb}
                      documentCounts={documentCounts}
                      countsLoading={countsLoading}
                      loadedFields={loadedFields}
                      fetchFieldDocuments={fetchFieldDocuments}
                      openTasks={openTasks}
                      completedTasks={completedTasks}
                      assigneeNames={assigneeNames}
                      showCompletedTasks={showCompletedTasks}
                      setShowCompletedTasks={setShowCompletedTasks}
                      setShowTaskForm={setShowTaskForm}
                      setSelectedTask={setSelectedTask}
                      setShowChangeOwner={setShowChangeOwner}
                      setShowChangeManager={setShowChangeManager}
                      setShowChangeProposalDesigner={setShowChangeProposalDesigner}
                      setShowChangeInstallerPartner={setShowChangeInstallerPartner}
                      setShowChangeInstallerPM={setShowChangeInstallerPM}
                      setShowChangeSurveyor={setShowChangeSurveyor}
                      setShowChangeBizDev={setShowChangeBizDev}
                      setShowChangeCloser={setShowChangeCloser}
                      setShowChangeSetter={setShowChangeSetter}
                      setProject={setProject}
                      handleClose={handleClose}
                      handleDelete={handleDelete}
                      onClose={onClose}
                      isOpen={isOpen}
                      onTaskSubmitted={onTaskSubmitted}
                      loading={loading}
                      derivedCommentTab={derivedCommentTab}
                      projectManagementRefreshKey={projectManagementRefreshKey}
                      documentUpdateTimestamp={documentUpdateTimestamp}
                      hasLeftFormTab={hasLeftFormTab}
                    />
                    {/* Drawer Stack Renderer - renders contained drawers over content */}
                    <DrawerRenderer />
                  </ModalContentArea>
                </div>
              )}
            </div>
          </>
        )}
        </DrawerStackProvider>
      </SlidePanel>

      {/* Assignment Panels - Consolidated Component */}
      <ProjectAssignmentPanels
        panelStates={assignmentPanelStates}
        searchStates={assignmentSearchStates}
        setSearch={setAssignmentSearch}
        closePanel={closeAssignmentPanel}
        project={project as ExtendedProject | null}
        allUsers={allUsers}
        eligibleUsers={eligibleUsers}
        followers={followers}
        handleChangeOwner={handleChangeOwner}
        handleChangeManager={handleChangeManager}
        handleChangeProposalDesigner={handleChangeProposalDesigner}
        handleChangeInstallerPartner={handleChangeInstallerPartner}
        handleChangeInstallerPM={handleChangeInstallerPM}
        handleChangeSurveyor={handleChangeSurveyor}
        handleChangeBizDev={handleChangeBizDev}
        handleChangeCloser={handleChangeCloser}
        handleChangeSetter={handleChangeSetter}
        addFollower={addFollower}
        removeFollower={removeFollower}
      />

      {stageDropdownOpen &&
        canMoveStageDirectly &&
        ReactDOM.createPortal(
          <div
            className="fixed bg-background border rounded shadow-lg z-[210] min-w-[200px] max-h-[300px] overflow-y-auto"
            style={{
              top: stageDropdownPosition.top,
              left: stageDropdownPosition.left,
              width: stageDropdownPosition.width,
            }}
          >
            <div className="py-1">
              {stages.map((stage) => (
                <div
                  key={stage.id}
                  className={`px-3 py-2 text-sm cursor-pointer hover:bg-muted ${project?.current_stage_id === stage.id ? "bg-muted font-medium" : ""}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleStageChange(stage.id);
                  }}
                >
                  {stage.name}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
      {/* Stage Slide Panel */}
      <ProjectStagePanel
        isOpen={showStageDialog}
        requestOnly={!canMoveStageDirectly}
        onClose={() => setShowStageDialog(false)}
        project={project}
        stages={stages}
        onStageChange={handleStageChange}
        onMoveToPipelineStage={handleMoveToPipelineStage}
      />
      {/* Status Slide Panel */}
      <ProjectStatusPanel
        isOpen={showStatusDialog}
        onClose={() => setShowStatusDialog(false)}
        project={project}
        onStatusChange={handleStatusChange}
      />

      {/* Document Gallery Viewer */}
      <DocumentGalleryViewer
        isOpen={galleryState.isOpen}
        onClose={() =>
          setGalleryState({ isOpen: false, documents: [], initialIndex: 0 })
        }
        documents={galleryState.documents}
        initialIndex={galleryState.initialIndex}
      />
    </>
  );
}
